// /var/www/oszczedzam/client/src/components/dashboard/UpcomingTransactions.js
import React from 'react';
import { 
  Box, Paper, Typography, List, ListItem, ListItemText, 
  Divider, Chip, ListItemIcon, Avatar, useTheme, alpha,
  Button
} from '@mui/material';
import { 
  TrendingUp as TrendingUpIcon, 
  TrendingDown as TrendingDownIcon,
  Event as EventIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function UpcomingTransactions({ transactions }) {
  const theme = useTheme();
  const navigate = useNavigate();
  
  // Formatuj kwotę
  const formatAmount = (amount) => {
    return amount.toLocaleString('pl-PL', { 
      style: 'currency', 
      currency: 'PLN',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2 
    });
  };
  
  // Formatuj datę
  const formatDate = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    const transactionDate = new Date(dateString);
    
    // Jeśli data to dziś
    if (transactionDate.getTime() === today.getTime()) {
      return 'Dziś';
    }
    
    // Jeśli data to jutro
    if (transactionDate.getTime() === tomorrow.getTime()) {
      return 'Jutro';
    }
    
    // W pozostałych przypadkach pokazuj normalną datę
    const options = { day: '2-digit', month: '2-digit' };
    return transactionDate.toLocaleDateString('pl-PL', options);
  };
  
  // Określ, ile dni pozostało do daty
  const getDaysLeft = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const transactionDate = new Date(dateString);
    
    // Oblicz różnicę w dniach
    const diffTime = transactionDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return '';
    } else if (diffDays === 1) {
      return '(jutro)';
    } else {
      return `(za ${diffDays} dni)`;
    }
  };
  
  return (
    <Paper 
      elevation={0} 
      sx={{ 
        p: 0, 
        mb: 4, 
        borderRadius: 3,
        boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid',
        borderColor: alpha(theme.palette.primary.main, 0.1),
        overflow: 'hidden'
      }}
    >
      <Box 
        sx={{ 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: theme.palette.divider
        }}
      >
        <Typography variant="h6" fontWeight="medium">Najbliższe transakcje</Typography>
        <Button 
          size="small"
          endIcon={<ChevronRightIcon />}
          onClick={() => navigate('/calendar')}
        >
          Wszystkie
        </Button>
      </Box>
      
      {transactions.length > 0 ? (
        <List disablePadding>
          {transactions.map((transaction, index) => (
            <React.Fragment key={transaction.id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                sx={{
                  py: 2,
                  px: 3,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar 
                    sx={{ 
                      width: 40, 
                      height: 40, 
                      bgcolor: transaction.amount > 0 
                        ? alpha(theme.palette.success.main, 0.1) 
                        : alpha(theme.palette.error.main, 0.1),
                      color: transaction.amount > 0 
                        ? theme.palette.success.main 
                        : theme.palette.error.main
                    }}
                  >
                    {transaction.amount > 0 ? (
                      <TrendingUpIcon />
                    ) : (
                      <TrendingDownIcon />
                    )}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="subtitle2" fontWeight="medium">
                        {transaction.title}
                      </Typography>
                      {transaction.isRecurring && (
                        <Chip 
                          icon={<EventIcon sx={{ fontSize: '0.8rem !important' }} />} 
                          label="Cykliczne" 
                          size="small" 
                          sx={{ 
                            ml: 1, 
                            height: 22, 
                            '& .MuiChip-label': { px: 1, fontSize: '0.7rem' }
                          }} 
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <React.Fragment>
                      <Box component="span" sx={{ display: 'block', color: 'text.secondary' }}>
                        {transaction.category} • {formatDate(transaction.date)} {' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          {getDaysLeft(transaction.date)}
                        </Typography>
                      </Box>
                      {transaction.description && (
                        <Box component="span" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
                          {transaction.description.length > 60 
                            ? `${transaction.description.substring(0, 60)}...` 
                            : transaction.description}
                        </Box>
                      )}
                    </React.Fragment>
                  }
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: transaction.amount > 0 ? 'success.main' : 'error.main',
                    fontWeight: 'medium',
                    whiteSpace: 'nowrap',
                    ml: 2
                  }}
                >
                  {formatAmount(transaction.amount)}
                </Typography>
              </ListItem>
            </React.Fragment>
          ))}
        </List>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4, px: 2 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            Brak nadchodzących transakcji
          </Typography>
          <Button 
            variant="outlined" 
            size="small"
            onClick={() => navigate('/transactions')}
          >
            Dodaj transakcję
          </Button>
        </Box>
      )}
    </Paper>
  );
}

export default UpcomingTransactions;